import researcherApi from '@/api/researcher'
import { STORAGE_KEYS } from '@/constants'

const state = {
  assignedStudies: [],
  notifications: [],
  currentPatientSummary: null,
  registrationStatus: null,
  loading: false,
  error: null
}

const getters = {
  // Use the root auth store for authentication state
  isAuthenticated: (state, getters, rootState, rootGetters) => rootGetters['auth/isAuthenticated'],
  currentResearcher: (state, getters, rootState, rootGetters) => rootGetters['auth/currentUser'],
  assignedStudies: state => state.assignedStudies,
  notifications: state => state.notifications,
  error: state => state.error,
  registrationStatus: state => state.registrationStatus,
  isLoading: state => state.loading,
  currentPatientSummary: state => state.currentPatientSummary,
}

const actions = {
  async getProfile({ commit, getters }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const researcher = getters.currentResearcher
      if (researcher?.id) {
        const updatedResearcher = await researcherApi.getResearcher(researcher.id)
        commit('auth/setAuth', {
          token: localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN),
          user: updatedResearcher
        }, { root: true })
      }
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.detail || error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getAssignedStudies({ commit, getters }) {
    const researcher = getters.currentResearcher
    if (!researcher) return

    commit('SET_LOADING', true)
    try {
      const response = await researcherApi.getStudies(researcher.id)
      commit('SET_STUDIES', response)
    } catch (error) {
      commit('SET_ERROR', error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async getNotifications({ commit, getters }) {
    const researcher = getters.currentResearcher
    if (!researcher) return

    commit('SET_LOADING', true)
    try {
      const response = await researcherApi.getNotifications(researcher.id)
      console.log('Notifications received:', response)
      commit('SET_NOTIFICATIONS', response)
    } catch (error) {
      commit('SET_ERROR', error.message)
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async verifyPhone({ commit, dispatch }, { email, verificationCode, session }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const response = await researcherApi.verifyPhone(email, verificationCode, session)

      // Unify authentication state in the root auth module
      commit('auth/setAuth', {
        token: response.access_token,
        user: response.researcher
      }, { root: true })

      // Store Cognito-specific tokens for future use
      if (response.id_token) localStorage.setItem(STORAGE_KEYS.RESEARCHER_ID_TOKEN, response.id_token)
      if (response.refresh_token) localStorage.setItem(STORAGE_KEYS.RESEARCHER_REFRESH_TOKEN, response.refresh_token)

      // Load initial data
      dispatch('getAssignedStudies')
      dispatch('getNotifications')

      return response
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.detail || error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async login({ commit, dispatch }, { email, password, mfaCode, session }) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      const response = await researcherApi.login(email, password, mfaCode, session)

      if (response.requires_mfa) {
        return { requires_mfa: true, session: response.session }
      }

      // Use researcher data from login response (includes full profile)
      const researcherData = response.researcher

      // Unify authentication state in the root auth module
      commit('auth/setAuth', {
        token: response.access_token,
        user: researcherData
      }, { root: true })

      // Store Cognito-specific tokens for future use (refreshes etc)
      if (response.id_token) localStorage.setItem(STORAGE_KEYS.RESEARCHER_ID_TOKEN, response.id_token)
      if (response.refresh_token) localStorage.setItem(STORAGE_KEYS.RESEARCHER_REFRESH_TOKEN, response.refresh_token)

      // Load initial data
      dispatch('getAssignedStudies')
      dispatch('getNotifications')

      return { success: true }
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.detail || error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async logout({ commit, dispatch }) {
    // Dispatch global logout to clear everything
    await dispatch('auth/logout', null, { root: true })

    commit('SET_STUDIES', [])
    commit('SET_NOTIFICATIONS', [])
    commit('SET_PATIENT_SUMMARY', null)
  },

  async fetchPatientSummary({ commit }, token) {
    commit('SET_LOADING', true)
    commit('SET_ERROR', null) // Clear previous errors
    try {
      const response = await researcherApi.getPatientSummary(token)
      commit('SET_PATIENT_SUMMARY', response)
      return response
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.detail || error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateReviewStatus({ commit }, { summaryId, reviewStatus }) {
    // Optimistically update the UI, then reconcile with the server response.
    const previousByNotificationId = {}
    commit('SET_NOTIFICATION_REVIEW_STATUS', { summaryId, reviewStatus, previousByNotificationId })
    try {
      const response = await researcherApi.updateReviewStatus(summaryId, reviewStatus)
      // Reconcile with the persisted value returned by the server.
      commit('SET_NOTIFICATION_REVIEW_STATUS', {
        summaryId,
        reviewStatus: response.review_status
      })
      return response
    } catch (error) {
      // Roll back the optimistic update.
      commit('REVERT_NOTIFICATION_REVIEW_STATUS', previousByNotificationId)
      commit('SET_ERROR', error.response?.data?.detail || error.message)
      throw error
    }
  },

}

const mutations = {
  SET_STUDIES(state, studies) {
    state.assignedStudies = studies
  },
  SET_NOTIFICATIONS(state, notifications) {
    state.notifications = notifications
  },
  SET_NOTIFICATION_REVIEW_STATUS(state, { summaryId, reviewStatus, previousByNotificationId }) {
    // Status is shared across all notifications referencing the same summary.
    state.notifications.forEach((n) => {
      if (n.patient_summary_id === summaryId) {
        if (previousByNotificationId) {
          previousByNotificationId[n.id] = n.review_status
        }
        n.review_status = reviewStatus
      }
    })
  },
  REVERT_NOTIFICATION_REVIEW_STATUS(state, previousByNotificationId) {
    state.notifications.forEach((n) => {
      if (Object.prototype.hasOwnProperty.call(previousByNotificationId, n.id)) {
        n.review_status = previousByNotificationId[n.id]
      }
    })
  },
  SET_PATIENT_SUMMARY(state, summary) {
    state.currentPatientSummary = summary
  },
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_REGISTRATION_STATUS(state, status) {
    state.registrationStatus = status
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
